import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { fmtRoDate } from "../lib/dateFmt";

// Helpers for default range = current month
const monthBounds = (d = new Date()) => {
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const iso = (x) => x.toISOString().slice(0, 10);
  return { from: iso(start), to: iso(end) };
};
const addMonths = (iso, delta) => {
  const d = new Date(iso + "T00:00:00");
  d.setMonth(d.getMonth() + delta);
  return monthBounds(d);
};
const monthLabel = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("ro-RO", { month: "long", year: "numeric" });
};

/**
 * Raport: litri alimentați per șofer pe interval
 *
 * Props:
 * - carId: opțional — dacă e prezent, raportul e filtrat pentru o singură mașină
 * - hideTitle: opțional — ascunde titlul (când e folosit în pagină proprie)
 */
export default function FuelByPersonReport({ carId, hideTitle = false }) {
  const initial = monthBounds();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (f, t) => {
    if (!f || !t) return;
    setLoading(true);
    try {
      const r = await api.get("/reports/fuel-by-person", { params: { from: f, to: t, ...(carId ? { car_id: carId } : {}) } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea raportului");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [carId]);

  useEffect(() => {
    search(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId]);

  const setMonth = (delta) => {
    const next = addMonths(from, delta);
    setFrom(next.from);
    setTo(next.to);
    search(next.from, next.to);
  };

  const setThisMonth = () => {
    const m = monthBounds();
    setFrom(m.from);
    setTo(m.to);
    search(m.from, m.to);
  };

  const maxLiters = data?.rows?.[0]?.total_liters || 0;

  return (
    <div className="card-flat p-6" data-testid="fuel-by-person-report">
      {!hideTitle && (
        <div className="mb-4">
          <h3 className="font-display text-lg font-bold">⛽ Raport alimentări per șofer</h3>
          <div className="text-xs text-zinc-500 mt-0.5">Total litri alimentați de fiecare persoană în intervalul selectat. {carId ? "Filtrat pentru această mașină." : "Pe toată flota."}</div>
        </div>
      )}

      {/* Period picker */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setMonth(-1)} data-testid="fuel-rep-prev-month" title="Luna anterioară">‹</Button>
          <Button variant="outline" size="sm" onClick={setThisMonth} data-testid="fuel-rep-this-month" className="font-bold">{monthLabel(from)}</Button>
          <Button variant="outline" size="sm" onClick={() => setMonth(1)} data-testid="fuel-rep-next-month" title="Luna următoare">›</Button>
        </div>
        <div className="text-xs text-zinc-400 mx-1">sau interval custom:</div>
        <div>
          <Label className="text-[11px] text-zinc-500">De la</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-[150px]" data-testid="fuel-rep-from"/>
        </div>
        <div>
          <Label className="text-[11px] text-zinc-500">Până la</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-[150px]" data-testid="fuel-rep-to"/>
        </div>
        <Button onClick={() => search(from, to)} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fuel-rep-search">
          {loading ? "Se încarcă…" : "Caută"}
        </Button>
      </div>

      {/* Totals */}
      {data && data.rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="fuel-rep-totals">
          <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total litri</div>
            <div className="font-display text-xl font-extrabold text-amber-600">{data.totals.liters} L</div>
          </div>
          <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Alimentări</div>
            <div className="font-display text-xl font-extrabold text-zinc-700">{data.totals.refuels}</div>
          </div>
          <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Șoferi</div>
            <div className="font-display text-xl font-extrabold text-blue-600">{data.totals.persons}</div>
          </div>
          <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{carId ? "Această mașină" : "Mașini"}</div>
            <div className="font-display text-xl font-extrabold text-zinc-700">{data.totals.cars}</div>
          </div>
        </div>
      )}

      {/* Rows */}
      {!data ? (
        <div className="text-zinc-400 text-sm">Se încarcă…</div>
      ) : data.rows.length === 0 ? (
        <div className="text-sm text-zinc-400 italic text-center py-8 border border-dashed border-zinc-200 rounded">
          Nicio alimentare înregistrată în {fmtRoDate(data.from)} – {fmtRoDate(data.to)}.
        </div>
      ) : (
        <div className="space-y-2">
          {data.rows.map((r, i) => {
            const pct = maxLiters > 0 ? Math.round((r.total_liters / maxLiters) * 100) : 0;
            return (
              <div key={r.person_id || `name-${i}`} className="rounded border border-zinc-200 overflow-hidden" data-testid={`fuel-rep-row-${i}`}>
                <div className="flex items-center gap-3 p-3">
                  {/* Rank */}
                  <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-extrabold flex-shrink-0 ${i === 0 ? "bg-amber-400 text-white" : i === 1 ? "bg-zinc-300 text-zinc-700" : i === 2 ? "bg-amber-700 text-white" : "bg-zinc-100 text-zinc-500"}`}>
                    {i + 1}
                  </div>
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full grid place-items-center font-bold text-sm flex-shrink-0" style={{ background: r.color || "#E5E7EB", color: r.color ? "white" : "#71717A" }}>
                    {(r.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {r.name}
                      {r.has_license && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider">🪪 Permis</span>}
                      {r.active === false && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600 font-bold">INACTIV</span>}
                      {!r.person_id && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold uppercase tracking-wider" title="Nume liber introdus în alimentare — nu am putut găsi persoana în baza de date">⚠️ neidentificat</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-3 flex-wrap">
                      {r.telefon && <span>📞 {r.telefon}</span>}
                      <span>{r.first_date && r.last_date ? (r.first_date === r.last_date ? fmtRoDate(r.first_date) : `${fmtRoDate(r.first_date)} → ${fmtRoDate(r.last_date)}`) : "—"}</span>
                      <span>{r.total_refuels} {r.total_refuels === 1 ? "alimentare" : "alimentări"}</span>
                    </div>
                    {/* Bar */}
                    <div className="mt-1.5 h-2 rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-display text-xl font-extrabold text-amber-600">{r.total_liters} <span className="text-xs text-zinc-500 font-normal">L</span></div>
                  </div>
                </div>

                {/* Per-car breakdown — only shown when not filtering by single car (or when 1 person has multiple cars) */}
                {!carId && r.cars.length > 0 && (
                  <div className="border-t border-zinc-100 bg-zinc-50/50 px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Mașini ({r.cars.length}):</div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.cars.map((car) => (
                        <div key={car.car_id} className="text-[11px] bg-white border border-zinc-200 rounded px-2 py-1 flex items-center gap-1.5" data-testid={`fuel-rep-row-${i}-car-${car.car_id}`}>
                          <span className="font-mono text-zinc-500">{car.label}</span>
                          <span className="text-zinc-300">·</span>
                          <span className="font-bold text-amber-600">{car.liters} L</span>
                          {car.refuels > 1 && <span className="text-zinc-400">({car.refuels}×)</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-chip breakdown per persoană */}
                {r.chips && r.chips.length > 0 && (
                  <div className="border-t border-zinc-100 bg-blue-50/40 px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1.5">CIP-uri folosite ({r.chips.length}):</div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.chips.map((ch) => (
                        <div key={ch.numar_chip} className="text-[11px] bg-white border border-blue-200 rounded px-2 py-1 flex items-center gap-1.5" data-testid={`fuel-rep-row-${i}-chip-${ch.numar_chip}`}>
                          <span className="font-mono text-blue-700">💳 {ch.numar_chip}</span>
                          <span className="text-zinc-300">·</span>
                          <span className="font-bold text-amber-600">{ch.liters} L</span>
                          {ch.refuels > 1 && <span className="text-zinc-400">({ch.refuels}×)</span>}
                          {ch.last_date && <span className="text-[10px] text-zinc-400 ml-1">ultim: {fmtRoDate(ch.last_date)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sumar global chips (cross-persoane) */}
      {data && data.chips && data.chips.length > 0 && (
        <div className="mt-6 border-t pt-4" data-testid="fuel-rep-chips-summary">
          <h4 className="font-display font-bold text-base mb-2 flex items-center gap-2">
            💳 Sumar pe CIP-uri ({data.chips.length})
            <span className="text-[11px] font-normal text-zinc-500">— total alimentări pe fiecare cip de combustibil</span>
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="p-2">Nr. Chip</th>
                  <th className="p-2 text-right">Litri total</th>
                  <th className="p-2 text-right">Alimentări</th>
                  <th className="p-2 text-right">Șoferi</th>
                  <th className="p-2 text-right">Mașini</th>
                  <th className="p-2">Perioadă</th>
                </tr>
              </thead>
              <tbody>
                {data.chips.map((ch) => (
                  <tr key={ch.numar_chip} className="border-t border-zinc-100 hover:bg-blue-50/30" data-testid={`fuel-rep-chip-${ch.numar_chip}`}>
                    <td className="p-2 font-mono font-bold text-blue-700">💳 {ch.numar_chip}</td>
                    <td className="p-2 text-right font-bold text-amber-600 tabular-nums">{ch.liters} L</td>
                    <td className="p-2 text-right tabular-nums">{ch.refuels}</td>
                    <td className="p-2 text-right tabular-nums">{ch.persons_count}</td>
                    <td className="p-2 text-right tabular-nums">{ch.cars_count}</td>
                    <td className="p-2 text-[11px] text-zinc-500">
                      {ch.first_date && ch.last_date ? (
                        ch.first_date === ch.last_date ? fmtRoDate(ch.first_date) : `${fmtRoDate(ch.first_date)} → ${fmtRoDate(ch.last_date)}`
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
